<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
  >
    <div :class="$style.inner">
      <div :class="$style.filters">
        <BaseSelect
          v-model="selectedSolutionTypeFilter"
          :class="$style.select"
          :placeholder="
            localizedStrings['all-solution-types'] || 'All Solution Types'
          "
          :options="computedSolutionTypes"
          :clearable="true"
          @on-change="setQuery"
          @on-clear="setQuery"
        />
        <BaseSelect
          v-model="selectedRegionFilter"
          :class="$style.select"
          :placeholder="localizedStrings['all-regions'] || 'All Regions'"
          :options="computedRegions"
          :clearable="true"
          @on-change="setQuery"
          @on-clear="setQuery"
        />
        <SearchField
          :initial-query="searchValue"
          :placeholder="localizedStrings['search'] || 'Search'"
          :class="$style.search"
          @request-query="handleSearch"
          @clear-query="handleSearch"
        />
      </div>
      <div v-if="computedRenderedPartners.length > 0" :class="$style.grid">
        <div
          v-for="(partner, i) in computedRenderedPartners"
          :key="partner?.sys?.id + i"
          :class="$style.card"
        >
          <div
            :class="$style.thumbnail"
            :style="{
              background: partner?.fields?.logoBackground || 'transparent',
            }"
          >
            <BasePicture
              :src="`${partner?.fields?.logoImage?.fields?.file?.url}?fit=thumb&q=90`"
              alt=""
            />
          </div>
          <div :class="$style.content">
            <div :class="$style.solutionTypes">
              <div
                v-for="solutionType in partner?.fields?.solutionTypes"
                :key="solutionType?.sys?.id"
                :class="$style.solutionType"
              >
                {{ solutionType?.fields?.label }}
              </div>
            </div>
            <div :class="$style.regions">
              <div
                v-for="region in partner?.fields?.regions"
                :key="region?.sys?.id"
                :class="$style.region"
              >
                {{ region?.fields?.label }}
              </div>
            </div>
            <BaseHeading size="h6" as="h3" :class="$style.name">
              {{ partner?.fields?.name }}
            </BaseHeading>
            <MarkedParser
              :md="partner?.fields?.copy"
              :class="['u-fs-p1', $style.copy]"
            />
            <div :class="$style.linksWrapper">
              <BaseButton
                v-if="!loggedIn"
                presentation="dark"
                size="default"
                :class="$style.signIn"
                @click="handleLogin"
              >
                {{
                  localizedStrings['sign-in-to-see-more'] ||
                  'Sign In to See More'
                }}
              </BaseButton>
              <div :class="[$style.links, !loggedIn && $style.masked]">
                <BaseButton
                  v-for="cta in partner?.fields?.links"
                  :key="cta?.sys?.id"
                  :to="loggedIn ? cta?.fields?.link : undefined"
                  presentation="default"
                  size="default"
                  :target="loggedIn ? cta?.fields?.target : undefined"
                  :class="$style.cta"
                >
                  {{ cta?.fields?.label }}
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="computedRenderedPartners.length < computedFilteredPartners.length"
        :class="$style.loadMoreWrapper"
      >
        <BaseButton presentation="outline" @click="handleLoadMore">
          {{ localizedStrings['load-more'] || 'Load More' }}
        </BaseButton>
      </div>
      <div v-if="!computedRenderedPartners.length" :class="$style.noResults">
        {{ localizedStrings['no-results'] || 'No Results' }}
      </div>
    </div>
  </WithBackdrop>
</template>

<script setup lang="ts">
import { instanceOfPartner } from '~/utils/instanceOfEntry'
import type { TypePreferredPartnersWithoutUnresolvableLinksResponse } from '~/types/contentful'

const props = defineProps<{
  data: TypePreferredPartnersWithoutUnresolvableLinksResponse
}>()

const { handleLogin } = useAuthLogin()
const route = useRoute()

const { $visibility } = useNuxtApp()
const userStore = useUserStore()
const { loggedIn } = storeToRefs(userStore)

const PAGE_SIZE = 8
const currentPage = ref(1)

const searchValue = ref(arrayAsString(route.query.q) || '')
const selectedSolutionTypeFilter = ref(
  arrayAsString(route.query.solution) || '',
)
const selectedRegionFilter = ref(arrayAsString(route.query.region) || '')

const { localizedStrings } = useLocalizedStrings()

const computedSolutionTypes = computed(() => {
  if (!props.data?.fields?.solutionTypeFilters) return []

  return [...props.data.fields.solutionTypeFilters]
    .map((tag) => {
      return {
        label: tag?.fields?.label || '',
        value: tag?.fields?.value || '',
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
})

const computedRegions = computed(() => {
  if (!props.data?.fields?.regionFilters) return []

  return [...props.data.fields.regionFilters]
    .map((tag) => {
      return {
        label: tag?.fields?.label || '',
        value: tag?.fields?.value || '',
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
})

const computedPartners = computed(() => {
  return props.data?.fields?.partners || []
})

const computedFilteredPartners = computed(() => {
  const partners = computedPartners.value?.filter(instanceOfPartner)

  const filtered = partners.filter((partner) => {
    const isVisible = $visibility(partner?.fields?.visibility)

    const hasSolutionType =
      !selectedSolutionTypeFilter.value ||
      (partner?.fields?.solutionTypes &&
        partner.fields.solutionTypes.findIndex(
          (solutionType) =>
            solutionType?.fields?.value === selectedSolutionTypeFilter.value,
        ) !== -1)

    const hasRegion =
      !selectedRegionFilter.value ||
      (partner?.fields?.regions &&
        partner.fields.regions.findIndex(
          (region) => region?.fields?.value === selectedRegionFilter.value,
        ) !== -1)

    const hasSearch =
      partner?.fields?.name
        ?.toLowerCase()
        .includes(searchValue.value?.toLowerCase()) ||
      partner?.fields?.copy
        ?.toLowerCase()
        .includes(searchValue.value?.toLowerCase())

    return isVisible && hasSolutionType && hasRegion && hasSearch
  })

  return filtered
})

const computedRenderedPartners = computed(() => {
  if (!computedFilteredPartners.value?.length) return []
  return computedFilteredPartners.value.slice(0, currentPage.value * PAGE_SIZE)
})

const handleSearch = (value: string) => {
  searchValue.value = value
  setQuery()
}

const handleLoadMore = () => {
  currentPage.value = currentPage.value + 1
}

const setQuery = () => {
  const router = useRouter()
  router.push({
    query: {
      q: searchValue.value || undefined,
      solution: selectedSolutionTypeFilter.value || undefined,
      region: selectedRegionFilter.value || undefined,
    },
  })
}

watch(
  () => [route.query.q, route.query.solution, route.query.region],
  async ([q, solution, region]) => {
    searchValue.value = arrayAsString(q) || ''
    selectedSolutionTypeFilter.value = arrayAsString(solution) || ''
    selectedRegionFilter.value = arrayAsString(region) || ''
    currentPage.value = 1
  },
  { immediate: true },
)
</script>

<style lang="scss" module>
.config {
  @include breakpoint-variables(
    '--pp-wrap-padding',
    (
      base: 45px 20px,
      md: 60px 20px,
      lg: 90px 20px,
    )
  );
}

.wrap {
  padding: var(--pp-wrap-padding);
}

.inner {
  margin: 0 auto;
  max-width: 1600px;
  min-height: 50vh;
}

.filters {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-bottom: 36px;

  @include media('sm') {
    grid-template-columns: repeat(3, 1fr);
  }

  @include media('lg') {
    grid-template-columns: repeat(4, 1fr);
  }
}

.search {
  width: 100% !important;
  grid-area: 1 / 1;

  @include media('sm') {
    grid-area: 1 / 1 / 1 / 4;
  }

  @include media('lg') {
    grid-area: auto;
  }
}

.noResults {
  text-align: center;
}

.grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;

  @include media('sm') {
    grid-template-columns: repeat(2, 1fr);
  }

  @include media('xl') {
    grid-template-columns: repeat(4, 1fr);
  }
}

.card {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--white);
}

.thumbnail {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 200px;
  padding: 24px;
  border-bottom: 1px solid var(--gray-2);
}

.content {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  padding: 24px;
  padding-top: 20px;
}

.solutionTypes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.solutionType {
  padding: 5px 12px;
  border-radius: 9999px;
  background: var(--gray-3);
  color: var(--purple);
  font-size: to-rem(14px);
}

.regions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.region {
  padding: 4px 12px;
  border: 1px solid var(--gray-7);
  border-radius: 9999px;
  color: var(--gray-7);
  font-weight: 600;
  font-size: to-rem(14px);
}

.name {
  margin-bottom: 4px;
}

.copy {
  margin-bottom: 20px;
}

.linksWrapper {
  position: relative;
  margin-top: auto;
}

.links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  &.masked {
    filter: blur(10px);
    opacity: 0.5;
    pointer-events: none;
  }
}

.cta {
  flex: 1 0 calc(50% - 4px);
  font-size: to-rem(16px) !important;
}

.signIn {
  z-index: 1;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 220px !important;
  max-width: 80% !important;
  font-size: to-rem(20px) !important;
}

.loadMoreWrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 48px;
}
</style>
